import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Transition } from "@headlessui/react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { toast } from "react-toastify";
import Loading from "assets/lottie/loading";
import {
    RamadanRiddlesCorrectAnswer,
    RamadanRiddlesGuide,
    RamadanRiddlesProgress,
    RamadanRiddlesQuestion,
    RamadanRiddlesRewards,
} from "./riddles-views";
import { RamadanRiddlesProvider, useRiddlesNavigation } from "./RamadanRiddlesContext";
import type { RamadanRiddlesView } from "./riddles-views/types";
import { IRamadanRiddlesResponse } from "@types";
import ramadanRiddlesApi from "@api/ramadanRiddlesApi";

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const VIEW_COMPONENTS: Record<RamadanRiddlesView, React.FC> = {
    progress: RamadanRiddlesProgress,
    guide: RamadanRiddlesGuide,
    question: RamadanRiddlesQuestion,
    "correct-answer": RamadanRiddlesCorrectAnswer,
    rewards: RamadanRiddlesRewards,
};

const RamadanRiddlesDrawerContent: React.FC = () => {
    const { currentView } = useRiddlesNavigation();
    const CurrentView = VIEW_COMPONENTS[currentView];

    return <CurrentView />;
};

const RamadanRiddlesDrawer: React.FC<Props> = ({ isOpen, onClose }) => {
    const t = useTranslations();
    const [overviewData, setOverviewData] = useState<IRamadanRiddlesResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const isFetchingRef = useRef(false);
    const isOpenRef = useRef(isOpen);
    const { locale } = useRouter();

    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);

    const fetchOverview = useCallback(async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        setIsLoading(true);

        try {
            const response = await ramadanRiddlesApi.getOverview().fetch();
            if (!isOpenRef.current) return;

            if (response?.meta?.code === 200) {
                setOverviewData(response.data);
            } else {
                toast.error(response?.meta?.msg || t("client_general_error"));
            }
        } catch (error) {
            console.error("Failed to load Ramadan riddles overview:", error);
            if (isOpenRef.current) {
                toast.error(t("client_general_error"));
            }
        } finally {
            if (isOpenRef.current) {
                setIsLoading(false);
            }
            isFetchingRef.current = false;
        }
    }, [t]);

    useEffect(() => {
        if (!isOpen) {
            setOverviewData(null);
            setIsLoading(false);
            return;
        }

        fetchOverview();
    }, [fetchOverview, isOpen]);

    return (
        <>
            <Transition
                show={isOpen}
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 backdrop-brightness-50 z-[58]" onClick={onClose} />
            </Transition>

            <Transition
                as={Fragment}
                show={isOpen}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-x-full"
                enterTo="opacity-100 translate-x-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-x-0"
                leaveTo="opacity-0 translate-x-full"
            >
                <div
                    dir="ltr"
                    className="fixed right-0 top-0 h-[100dvh] w-screen md:max-w-[550px] bg-[#F6EAD5] z-[59] overflow-y-auto flex flex-col"
                >
                    <div
                        dir={locale?.startsWith("ar-") ? "rtl" : "ltr"}
                        className="flex flex-1 flex-col"
                    >
                        {overviewData ? (
                            <RamadanRiddlesProvider
                                onClose={onClose}
                                initialOverview={overviewData}
                            >
                                <RamadanRiddlesDrawerContent />
                            </RamadanRiddlesProvider>
                        ) : isLoading ? null : (
                            <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                                <p className="text-sm text-gray-500">{t("client_general_error")}</p>
                                <button
                                    type="button"
                                    onClick={fetchOverview}
                                    className="h-11 rounded-full bg-primary px-6 text-sm font-bold text-white"
                                >
                                    {t("Try again")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </Transition>
            {isLoading ? <Loading /> : null}
        </>
    );
};

export default RamadanRiddlesDrawer;
